import React, { useState, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppContext } from '../AppContext';
import { Phone, Mail, MapPin, Send, Check, Clock, ExternalLink, MessageCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { showSuccessAlert } from '../sweetAlert';
import './ContactPage.css';

const ContactPage = () => {
  const { addInquiry, lang } = useContext(AppContext);
  const isMr = lang === 'mr';
  const [searchParams] = useSearchParams();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: searchParams.get('subject') || 'General Enquiry',
    message: searchParams.get('subject') ? `I would like to order: ${searchParams.get('subject')}` : ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [activeField, setActiveField] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    addInquiry(formData);
    // Also send email via mailto
    const mailtoBody = encodeURIComponent(
      `Name: ${formData.name}\nPhone: ${formData.phone}\nMessage: ${formData.message}`
    );
    window.open(`mailto:gdrjwell@gmail.com?subject=${encodeURIComponent(formData.subject)}&body=${mailtoBody}`);
    setSubmitted(true);
    showSuccessAlert(
      isMr ? 'संदेश यशस्वीरीत्या पाठवला!' : 'Message Sent Successfully!',
      isMr ? 'तुमची विचारणा नोंदवली गेली आहे. आम्ही लवकरच संपर्क करू.' : 'Your inquiry has been received. Our team will contact you shortly.'
    );
    setFormData({ name: '', email: '', phone: '', subject: 'General Enquiry', message: '' });
    setTimeout(() => setSubmitted(false), 5000);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const CONTACT_CARDS = [
    { icon: <Phone size={22} />, label: isMr ? 'आम्हाला कॉल करा' : 'Call Us', value: '+91 91724 55555', sub: '+91 251 2444321', href: 'tel:+919172455555', color: '#d4af37' },
    { icon: <Mail size={22} />, label: isMr ? 'आम्हाला ईमेल करा' : 'Email Us', value: 'gdrjwell@gmail.com', sub: 'info@gdrjewellers.com', href: 'mailto:gdrjwell@gmail.com', color: '#b8860b' },
    { icon: <MapPin size={22} />, label: isMr ? 'भेट द्या' : 'Visit Us', value: isMr ? 'जीडीआर सिल्व्हर' : 'GDR SILVER', sub: isMr ? 'हुपरी, कोल्हापूर, महाराष्ट्र' : 'Hupari, Kolhapur, Maharashtra', href: 'https://www.google.com/maps/place/GDR+SILVER/@16.6156618,74.4059449,19.03z/', color: '#8c6e3d' },
    { icon: <Clock size={22} />, label: isMr ? 'स्टोअरची वेळ' : 'Store Hours', value: isMr ? 'सोम - रवि' : 'Mon – Sun', sub: '10:30 AM – 8:30 PM', href: null, color: '#7a5a3c' }
  ];

  return (
    <div className="contact-page">
      {/* Hero with inline img */}
      <section className="contact-hero">
        <div className="contact-hero-bg">
          <img src="images/banner c.png" alt="GDR Jewellers Contact Banner" />
          <div className="contact-hero-overlay"></div>
        </div>
        <motion.div
          className="contact-hero-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <h1>{isMr ? 'जीडीआर सोबत कनेक्ट व्हा' : 'Connect With GDR'}</h1>
          <p>{isMr ? 'आमच्या शोरूमला भेट द्या किंवा परिपूर्ण दागिना शोधण्यासाठी आमच्या मास्टर ज्वेलरचा सल्ला घ्या.' : 'Visit our showroom or consult with our master jewellers to discover your perfect piece'}</p>
        </motion.div>
      </section>

      {/* Contact Cards Row */}
      <section className="contact-cards-strip">
        <div className="contact-cards-container">
          {CONTACT_CARDS.map((card, i) => (
            <motion.div
              key={i}
              className="contact-card-item"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              whileHover={{ y: -6 }}
            >
              <div className="contact-card-icon" style={{ '--icon-color': card.color }}>
                {card.icon}
              </div>
              <div className="contact-card-text">
                <span className="contact-card-label">{card.label}</span>
                {card.href ? (
                  <a href={card.href} target={card.href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer">
                    {card.value}
                  </a>
                ) : (
                  <strong>{card.value}</strong>
                )}
                <span className="contact-card-sub">{card.sub}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Main Form + Info Grid */}
      <section className="contact-section">
        <div className="container">
          <div className="contact-grid">

            {/* Left: Info Column */}
            <div className="contact-info-col">
              <span className="info-pretitle">✦ {isMr ? 'जीडीआर ज्वेलर्स' : 'GDR JEWELLERS'}</span>
              <h2>{isMr ? 'चला एकत्र काहीतरी विलक्षण बनवूया' : 'Let\'s Create Something Extraordinary Together'}</h2>
              <p className="info-lead">{isMr ? 'कस्टम ब्राइडल पीस असो, गिफ्टिंग सल्लामसलत असो किंवा आकाराबद्दल प्रश्न असो — आम्ही प्रत्येक पावलावर तुमच्यासोबत आहोत.' : 'Whether it\'s a custom bridal piece, a gifting consultation, or just a question about sizing — we\'re here for you every step of the way.'}</p>

              <div className="info-divider"></div>

              <div className="social-reach-section">
                <h4>{isMr ? 'सोशल वर कनेक्ट व्हा' : 'Connect on Social'}</h4>
                <div className="social-links-col">
                  <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="social-link-row">
                    <ExternalLink size={18} /> {isMr ? 'इंस्टाग्राम वर फॉलो करा' : 'Follow on Instagram'}
                  </a>
                  <a href="https://wa.me/919172455555" target="_blank" rel="noopener noreferrer" className="social-link-row whatsapp">
                    <MessageCircle size={18} /> {isMr ? 'व्हॉट्सॲप वर चॅट करा' : 'Chat on WhatsApp'}
                  </a>
                </div>
              </div>

              <div className="info-testimonial">
                <p>{isMr ? '"जीडीआरने आमची एंगेजमेंट रिंग इतक्या काळजीने बनवली — प्रत्येक तपशील परिपूर्ण होता."' : '"GDR crafted our engagement rings with such care — every detail was perfect. Truly heirloom quality."'}</p>
              </div>
            </div>

            {/* Right: Form */}
            <div className="contact-form-col">
              {submitted ? (
                <motion.div
                  className="success-message-box"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                >
                  <div className="success-icon"><Check size={32} /></div>
                  <h3>{isMr ? 'चौकशी पाठवली! ✓' : 'Enquiry Sent! ✓'}</h3>
                  <p>{isMr ? 'जीडीआर ज्वेलर्सशी संपर्क साधल्याबद्दल धन्यवाद. आमचे तज्ञ 24 तासांच्या आत उत्तर देतील.' : 'Thank you for reaching out to GDR Jewellers. Your message has also been sent via email. One of our experts will reply within 24 hours.'}</p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="premium-contact-form">
                  <div className="form-header">
                    <h3>{isMr ? 'संदेश पाठवा' : 'Send a Message'}</h3>
                    <p>{isMr ? 'सर्व चौकशींना 24 कामाच्या तासांत प्रतिसाद दिला जातो.' : 'All enquiries are responded to within 24 working hours.'}</p>
                  </div>

                  <div className={`form-group ${activeField === 'name' ? 'focused' : ''}`}>
                    <label>{isMr ? 'पूर्ण नाव' : 'Full Name'} <span className="req">*</span></label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      onFocus={() => setActiveField('name')}
                      onBlur={() => setActiveField('')}
                      required
                      placeholder={isMr ? "उदा. प्रिया शर्मा" : "e.g. Priya Sharma"}
                    />
                  </div>

                  <div className="form-row">
                    <div className={`form-group ${activeField === 'email' ? 'focused' : ''}`}>
                      <label>{isMr ? 'ईमेल' : 'Email'} <span className="req">*</span></label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        onFocus={() => setActiveField('email')}
                        onBlur={() => setActiveField('')}
                        required
                        placeholder="name@domain.com"
                      />
                    </div>
                    <div className={`form-group ${activeField === 'phone' ? 'focused' : ''}`}>
                      <label>{isMr ? 'फोन' : 'Phone'} <span className="req">*</span></label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        onFocus={() => setActiveField('phone')}
                        onBlur={() => setActiveField('')}
                        required
                        placeholder="+91 99999 99999"
                      />
                    </div>
                  </div>

                  <div className={`form-group ${activeField === 'subject' ? 'focused' : ''}`}>
                    <label>{isMr ? 'विषय' : 'Subject'}</label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      onFocus={() => setActiveField('subject')}
                      onBlur={() => setActiveField('')}
                    >
                      <option>{isMr ? 'सामान्य चौकशी' : 'General Enquiry'}</option>
                      <option>{isMr ? 'सानुकूल डिझाइन विनंती' : 'Custom Design Request'}</option>
                      <option>{isMr ? 'आकार सल्लामसलत' : 'Gold Sizing Consultation'}</option>
                      <option>{isMr ? 'गिफ्टिंग सल्लामसलत' : 'Gifting Consultation'}</option>
                      <option>{isMr ? 'ऑर्डरसाठी' : 'Buy Now Order'}</option>
                      <option>{isMr ? 'मोठ्या प्रमाणात / कॉर्पोरेट ऑर्डर्स' : 'Bulk / Corporate Orders'}</option>
                    </select>
                  </div>

                  <div className={`form-group ${activeField === 'message' ? 'focused' : ''}`}>
                    <label>{isMr ? 'तुमचा संदेश' : 'Your Message'} <span className="req">*</span></label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      onFocus={() => setActiveField('message')}
                      onBlur={() => setActiveField('')}
                      required
                      placeholder={isMr ? "तुमची दागिन्यांची विनंती, आकाराच्या आवश्यकता किंवा गिफ्टिंगची गरज सांगा..." : "Describe your jewellery request, sizing requirements, or gifting need..."}
                      rows={5}
                    />
                  </div>

                  <button type="submit" className="btn-submit-form">
                    <Send size={16} /> {isMr ? 'चौकशी पाठवा' : 'Submit Enquiry'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Real Google Map Embed Section */}
      <section className="showroom-map-section">
        <div className="map-section-header">
          <span className="gold-sub-label">{isMr ? 'आम्हाला शोधा' : 'FIND US'}</span>
          <h3>{isMr ? 'आमचे प्रमुख शोरूम शोधा' : 'Locate Our Flagship Showroom'}</h3>
        </div>
        <div className="map-fullwidth-container">
          <iframe
            title="GDR Silver Google Maps"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d934.9309967812806!2d74.40594487611823!3d16.61566181369137!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc0e327e8ac74f5%3A0xd2dfdc44d788e70b!2sGDR%20SILVER!5e0!3m2!1sen!2sin!4v1789626639386!5m2!1sen!2sin"
            width="100%"
            height="500"
            style={{ border: 0, display: 'block' }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
          ></iframe>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
